import useNavigation, { useRouter } from "../AppDrawerComponent/Navigation";

// Presentational-only grouping. All visibility/permission filtering is done
// by useNavigation() (Navigation.js) — this file never decides what a role
// can see, it only decides which header a title appears under.
const SECTION_MAP = {
  Dashboard: "OVERVIEW",
  Orders: "ORDERS",
  "Pending RSC Quotes": "ORDERS",
  "Sell Catalog": "CATALOG",
  "Brands & Sets": "CATALOG",
  "Ship Direct": "CATALOG",
  "Gift Cards": "CATALOG",
  "Shipping & Tracking": "LOGISTICS",
  Inbound: "LOGISTICS",
  Inventory: "LOGISTICS",
  Payouts: "FINANCE",
  "Withdraw Requests": "FINANCE",
  "User Management": "ADMIN",
  "Terms & Conditions": "ADMIN",
  "Privacy Policy": "ADMIN",
};
const SECTION_ORDER = ["OVERVIEW", "ORDERS", "CATALOG", "LOGISTICS", "FINANCE", "ADMIN", "MORE"];

const GLYPH_MAP = {
  Dashboard: "▦",
  Orders: "⬒",
  "Live Orders": "⬒",
  "Open Orders": "⬒",
  "Completed Orders": "✓",
  "Pending RSC Quotes": "❝",
  Quotes: "❝",
  "Sell Catalog": "⚡",
  "Brands & Sets": "⊞",
  "Ship Direct": "➤",
  "Gift Cards": "🎴",
  "Shipping & Tracking": "📦",
  Shipments: "📦",
  Payouts: "◈",
  "Wallet Credit": "◈",
  "Withdraw Requests": "⇣",
  "Payment Resolution": "!",
  Inbound: "⬇",
  Inventory: "▤",
  "User Management": "♙",
  "Terms & Conditions": "§",
  "Privacy Policy": "§",
  "Help & Support": "?",
};

function resolvePath(segment) {
  if (!segment) return null;
  return segment.startsWith("/") ? segment : `/${segment}`;
}

function resolveChildPath(parentPath, childSegment) {
  if (!childSegment) return parentPath;
  return childSegment.startsWith("/")
    ? `${parentPath}${childSegment}`
    : `${parentPath}/${childSegment}`;
}

// Wraps the existing useNavigation() hook (unchanged) and reshapes its flat,
// already role/permission-filtered array into labeled sections for the
// sidebar. Items without a `title` (the Logout action entry) are dropped —
// ManagementSidebar renders its own logout row, matching MemberSidebar.
export function useManagementNavSections() {
  const rawItems = useNavigation() || [];

  const sections = {};
  for (const item of rawItems) {
    if (!item.title) continue;

    const parentPath = resolvePath(item.segment);
    const children = item.children
      ?.filter((child) => child.title)
      .map((child) => ({
        title: child.title,
        path: resolveChildPath(parentPath, child.segment),
      }));

    const navItem = {
      title: item.title,
      path: parentPath,
      icon: GLYPH_MAP[item.title] || "•",
      badge: item.badge,
      children: children?.length ? children : undefined,
    };

    const sectionLabel = SECTION_MAP[item.title] || "MORE";
    if (!sections[sectionLabel]) sections[sectionLabel] = [];
    sections[sectionLabel].push(navItem);
  }

  // Group T&C and Privacy Policy into a single collapsible "Policies" parent,
  // matching the member sidebar's "Policies" expandable item in SIDEBAR_SECTIONS.
  if (sections["ADMIN"]) {
    const tncIdx = sections["ADMIN"].findIndex((i) => i.title === "Terms & Conditions");
    const ppIdx = sections["ADMIN"].findIndex((i) => i.title === "Privacy Policy");
    if (tncIdx !== -1 && ppIdx !== -1) {
      const tnc = sections["ADMIN"][tncIdx];
      const pp = sections["ADMIN"][ppIdx];
      const policiesItem = {
        title: "Policies",
        path: tnc.path,
        icon: "§",
        children: [
          { title: tnc.title, path: tnc.path },
          { title: pp.title, path: pp.path },
        ],
      };
      const insertAt = Math.min(tncIdx, ppIdx);
      sections["ADMIN"] = sections["ADMIN"].filter(
        (i) => i.title !== "Terms & Conditions" && i.title !== "Privacy Policy"
      );
      sections["ADMIN"].splice(insertAt, 0, policiesItem);
    }
  }

  return SECTION_ORDER.filter((label) => sections[label]?.length).map((label) => ({
    label,
    items: sections[label],
  }));
}

export { useRouter };
